/**
 * The producer shell (docs/06 §4): left sidebar — cabinet home, profile, products, orders,
 * notifications; top bar with the "Connect Telegram" state chip, the language switcher, and the
 * account identity.
 *
 * VISUALLY this is the ADMIN shell (apps/admin/src/components/shell/): a near-black rail on the
 * left, and the workspace as a rounded parchment panel inset into the dark ground (`p-2` + a
 * radius is the whole trick — the dark edge showing along the right and bottom is what makes the
 * work surface read as a card lying ON something). The two cabinets share one design system.
 *
 * Desktop-first, target ≥ 1024 (docs/06 §9). It must remain USABLE down to 768, where the
 * sidebar collapses to icons. Below that it still works — it is simply not optimized, which is
 * the documented MVP position for the producer cabinet.
 */
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageTransition } from '@most/ui';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Boxes,
  Building2,
  ClipboardList,
  LayoutDashboard,
  Loader2,
  LogOut,
  Send,
} from 'lucide-react';
import { Badge, cn } from '@/components/ui';
import { useChangeLocale, useLogout, useMe } from '@/shared/session';
import { useNotificationSocket } from '@/shared/ws';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { LocaleTabs } from '@/components/LocaleTabs';
import { SupportChat } from '@/components/SupportChat';

const NAV = [
  { to: '/', end: true, icon: LayoutDashboard, key: 'nav.home' },
  { to: '/profile', end: false, icon: Building2, key: 'nav.profile' },
  { to: '/products', end: false, icon: Boxes, key: 'nav.products' },
  { to: '/orders', end: false, icon: ClipboardList, key: 'nav.orders' },
  { to: '/notifications', end: false, icon: Bell, key: 'nav.notifications' },
] as const;

/**
 * The unread counter, in the admin rail's language: on near-black a pale tint is invisible, so
 * the chip is a saturated amber fill with dark text (see the admin Sidebar for the long version).
 */
function Counter({ value }: { value: number }) {
  if (!value) return null;
  return (
    <span className="tabular ml-auto hidden min-w-[1.375rem] items-center justify-center rounded bg-amber-300 px-1.5 py-0.5 text-2xs font-semibold tabular-nums text-amber-950 lg:flex">
      {value > 99 ? '99+' : value}
    </span>
  );
}

export function Shell() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const changeLocale = useChangeLocale();

  // Live notifications. Only once we know who we are — an anonymous socket is pointless.
  useNotificationSocket(Boolean(me));

  const { data: telegram } = useQuery({
    queryKey: qk.telegramStatus,
    queryFn: api.getTelegramStatus,
    staleTime: 30_000,
  });

  const unread = me?.unread_notifications ?? 0;

  return (
    <div className="flex h-screen overflow-hidden bg-rail">
      {/* ── The rail. Icons-only below lg (docs/06 §9: "sidebar collapses to icons"). ── */}
      <nav
        aria-label={t('common.cabinet')}
        className="flex h-full w-16 shrink-0 flex-col bg-rail text-rail-foreground lg:w-60"
      >
        <div className="flex h-16 shrink-0 items-center justify-center gap-2.5 px-3 lg:justify-start lg:px-5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary text-xs font-bold text-primary-foreground">
            M
          </span>
          {/* No "producer" chip next to the wordmark: unlike Merdan, a producer never switches
              apps, and the full "Кабинет производителя" does not fit a 15rem rail untruncated. */}
          <span
            className="hidden text-base font-semibold tracking-tight text-rail-foreground lg:inline"
            title={t('common.cabinet')}
          >
            MOST
          </span>
        </div>

        <div className="rail-scroll flex-1 space-y-0.5 overflow-y-auto px-2 pb-4 pt-2 lg:px-3">
          {NAV.map(({ to, end, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-2.5 rounded px-2.5 py-2 text-sm transition-colors',
                  'justify-center lg:justify-start',
                  isActive
                    ? // On a dark rail the active item is a LIGHT plate — the figure-ground flip
                      // does the work a colour accent used to.
                      'bg-rail-active font-semibold text-rail'
                    : 'text-rail-foreground/75 hover:bg-rail-hover hover:text-rail-foreground',
                )
              }
              // Below lg the label is hidden, so the icon needs an accessible name.
              title={t(key)}
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive ? 'text-rail' : 'text-rail-muted group-hover:text-rail-foreground',
                    )}
                    aria-hidden
                  />
                  <span className="hidden truncate lg:inline">{t(key)}</span>
                  {key === 'nav.notifications' ? <Counter value={unread} /> : null}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <div className="border-t border-rail-border p-2 lg:p-3">
          <button
            type="button"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            title={t('common.logout')}
            className={cn(
              'flex w-full items-center gap-2.5 rounded px-2.5 py-2 text-sm transition-colors',
              'justify-center lg:justify-start',
              'text-rail-foreground/75 hover:bg-rail-hover hover:text-rail-foreground',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {logout.isPending ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <LogOut className="h-4 w-4 shrink-0 text-rail-muted" aria-hidden />
            )}
            <span className="hidden lg:inline">{t('common.logout')}</span>
          </button>
        </div>
      </nav>

      {/* ── The workspace: a rounded parchment panel inset into the dark shell. ── */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden py-2 pr-2">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded bg-app">
          {/* The bar sits ON the canvas — no card background, no bottom rule (the panel already
              has an edge). `bg-app/80 + backdrop-blur` keeps rows legible as they scroll under. */}
          <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 bg-app/80 px-4 backdrop-blur lg:px-6">
            {/* Telegram state chip. Unlinked is NOT an error — delivery falls back silently to
                email + in-app (docs/04 §6). It is a nudge, never a warning. */}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-app"
            >
              {telegram?.linked ? (
                <Badge tone="green">
                  <Send className="h-3 w-3" aria-hidden />
                  {t('home.telegram.linked')}
                </Badge>
              ) : (
                <Badge tone="neutral">
                  <Send className="h-3 w-3" aria-hidden />
                  {t('home.telegram.connect')}
                </Badge>
              )}
            </button>

            <div className="ml-auto flex items-center gap-2">
              <LocaleTabs
                value={
                  i18n.language.startsWith('tk') ? 'tk' : i18n.language.startsWith('en') ? 'en' : 'ru'
                }
                onChange={(locale) => changeLocale.mutate(locale)}
              />

              {me ? (
                <Link
                  to="/profile"
                  title={t('nav.profile')}
                  className="hidden max-w-[16rem] truncate rounded text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-app md:inline"
                >
                  {me.email}
                </Link>
              ) : null}
            </div>
          </header>

          {/* `scroll-main`: the shell is overflow-hidden, so <main> is the app's page scrollbar. */}
          <main className="scroll-main min-w-0 flex-1 overflow-y-auto">
            <PageTransition routeKey={location.pathname} className="px-4 pb-8 pt-2 lg:px-8">
              <Outlet />
            </PageTransition>
          </main>
        </div>
      </div>
      {me ? <SupportChat /> : null}
    </div>
  );
}

/**
 * Neutral terminal screen for a blocked account. The server has already revoked the session, so
 * there is nothing to show and nothing to do here — and deliberately no explanation of WHY, per
 * docs/06 §3 ("neutral 'account unavailable, contact MOST'").
 */
export function BlockedScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-app p-6">
      <div className="max-w-md rounded border border-border bg-card p-8 text-center">
        <h1 className="text-lg font-semibold text-foreground">Аккаунт недоступен</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Обратитесь в MOST для восстановления доступа.
        </p>
      </div>
    </div>
  );
}

/** Route-level fallback while the session bootstrap (`GET /me`) is in flight. */
export function ShellSkeleton() {
  return (
    <div className="flex h-screen overflow-hidden bg-rail">
      <div className="h-full w-16 shrink-0 lg:w-60" />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden py-2 pr-2">
        <div className="flex-1 rounded bg-app">
          <div className="space-y-4 p-8">
            <div className="skeleton h-8 w-64" />
            <div className="skeleton h-32 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
